import React, { useState } from 'react';
import { CampaignIO, IOLineItem } from 'shared/src/api/io-types';
import { formatDate } from 'shared/src/utils/date/date-utils';
import { UTCDate } from '@date-fns/utc';
import { formatCurrency } from '@/components/tables/table-utils';
import { Button } from '@/components/forms/ui/button';
import { IoTypeBadge } from './io-type-badge';
import { IoStatusBadge } from './io-status-badge';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { diffInDays } from '@/metrics/calculate-metrics';
import { getUnitName } from 'shared/src/domain/line-item/line-item-utils';
import { UnitPriceType } from 'shared/src/domain/line-item/line-item-channels';

type IoSummaryCardProps = {
  io: CampaignIO;
};

export function IoSummaryCard({ io }: IoSummaryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasTotal = io.io_total !== null;
  const formattedTotal = hasTotal ? formatCurrency(io.io_total!) : 'No Line Item prices';
  const zohoUrl = `https://crm.zoho.com/crm/org643932192/tab/CustomModule19/${io.io_id}`;

  const hasDateRange = io.start_date && io.end_date;
  const dateRange =
    io.start_date && io.end_date
      ? `${formatDate(new UTCDate(io.start_date), 'MMM d yyyy')} – ${formatDate(new UTCDate(io.end_date), 'MMM d yyyy')}`
      : 'No Line Item dates';

  const hasLineItems = io.line_item_count > 0;

  const handleLineItemsClick = () => {
    if (hasLineItems) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className="group relative flex flex-col gap-1.5 rounded-lg border border-gray-300 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1 truncate text-sm font-bold text-gray-800 group-hover:mr-[100px]">
          {io.io_name}
        </div>
        <Button
          variant="blue"
          asChild
          className="absolute right-2 h-auto rounded-sm px-2 py-1 text-xs opacity-0 transition-opacity group-hover:opacity-100">
          <a href={zohoUrl} target="_blank" rel="noopener noreferrer">
            View in Zoho
          </a>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <IoTypeBadge ioType={io.io_type} ioNumber={io.io_number} />
        <IoStatusBadge status={io.status} completionDate={io.completion_date} />
      </div>
      {hasLineItems && (
        <div className="flex items-center justify-between">
          <span
            className={hasTotal ? 'text-sm font-semibold text-gray-800' : 'text-xs text-gray-600'}>
            {formattedTotal}
          </span>
          <span className={hasDateRange ? 'text-xs text-gray-800' : 'text-xs text-gray-600'}>
            {dateRange}
          </span>
        </div>
      )}
      <div
        className={`flex items-center justify-between rounded bg-gray-100 px-2 py-1 ${hasLineItems ? 'cursor-pointer hover:bg-gray-200' : ''}`}
        onClick={handleLineItemsClick}>
        <span className="text-xs text-gray-600">
          <span className="font-bold">{io.line_item_count}</span> Line Items
        </span>
        {hasLineItems && (
          <span className="text-gray-500">
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
        )}
      </div>
      {isExpanded && hasLineItems && (
        <div className="flex flex-col">
          {io.line_items.map(lineItem => (
            <LineItemRow key={lineItem.id} lineItem={lineItem} />
          ))}
          {(io.io_created_time || io.created_by_name) && (
            <div className="py-1 text-center text-xs text-gray-500">
              IO Created{' '}
              {io.io_created_time && formatDate(new UTCDate(io.io_created_time), 'MMM d yyyy')}
              {io.created_by_name && ` by ${io.created_by_name}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LineItemRow({ lineItem }: { lineItem: IOLineItem }) {
  const hasDateRange = lineItem.start_date && lineItem.end_date;
  const dateRange = hasDateRange
    ? `${formatDate(new UTCDate(lineItem.start_date!), 'MMM d yyyy')} – ${formatDate(new UTCDate(lineItem.end_date!), 'MMM d yyyy')}`
    : 'No Line Item dates';
  const daysCount = hasDateRange
    ? `${diffInDays(new UTCDate(lineItem.start_date!), new UTCDate(lineItem.end_date!))} Days`
    : '-- Days';

  return (
    <div className="flex flex-col gap-1 border-t border-gray-200 py-1">
      <div className="truncate text-sm font-semibold text-gray-800">
        {lineItem.name || 'No Line Item name in Bravo'}
      </div>
      {lineItem.tactics && (
        <div className="text-xs font-medium text-gray-500">{lineItem.tactics}</div>
      )}
      <div className="flex items-center justify-between text-xs">
        <span className={hasDateRange ? 'text-gray-800' : 'text-gray-500'}>{dateRange}</span>
        <span className={hasDateRange ? 'text-gray-800' : 'text-gray-500'}>{daysCount}</span>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-gray-800">{formatLineItemPrice(lineItem.price)}</span>
        <span className="text-gray-600">
          {formatUnitPrice(lineItem.unit_price, lineItem.unit_price_type)}
        </span>
        <span className="text-gray-600">
          {formatEstimatedUnits(lineItem.estimated_units, lineItem.unit_price_type)}
        </span>
      </div>
    </div>
  );
}

function formatLineItemPrice(price: string | null): string {
  if (price == null) return '—';
  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) return '—';
  return formatCurrency(numPrice);
}

function formatUnitPrice(unitPrice: string | null, unitPriceType: string | null): string {
  if (unitPriceType === 'Flat Fee') return 'Flat Fee';
  if (unitPrice == null) return '—';
  const numPrice = parseFloat(unitPrice);
  if (isNaN(numPrice)) return '—';
  const formatted = formatCurrency(numPrice);
  return unitPriceType ? `${formatted} ${unitPriceType}` : formatted;
}

function formatEstimatedUnits(estimatedUnits: string | null, unitPriceType: string | null): string {
  if (unitPriceType === 'Flat Fee') return '-';
  if (!estimatedUnits) return '-';
  const numUnits = parseFloat(estimatedUnits);
  if (isNaN(numUnits)) return '-';
  const formatted = numUnits.toLocaleString();
  if (unitPriceType) {
    const unitName = getUnitName(unitPriceType as UnitPriceType['name']);
    return `${formatted} ${unitName.abbr}`;
  }
  return formatted;
}
